import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { TpaLiveStatus } from '@/components/tagadapay/tpa-live-status'
import { ProcessorDetails } from '@/lib/api/processors'
import { useTagadapayAccounts } from '@/lib/api/processors'
import { processingNetworkLabel } from '@/lib/processing-network'
import { tpaIdFromProcessor } from '@/lib/tagadapay-tpa-status'

interface TagadaPayRouterConfigurationCardProps {
    processor: ProcessorDetails
}

const THREE_DS_MODE_LABELS: Record<string, string> = {
    sca_only: 'SCA only (EEA + UK)',
    always: 'Always',
    never: 'Never',
    prefer_no: 'Prefer no challenge',
    prefer_yes: 'Prefer challenge',
}

export const TagadaPayRouterConfigurationCard = ({ processor }: TagadaPayRouterConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const options = processor.options as Record<string, unknown> | null
    const accountId = tpaIdFromProcessor(processor) ?? undefined

    const threeDSMode = (options?.threeDSMode as string | undefined) ?? 'sca_only'

    const { data: accounts } = useTagadapayAccounts()
    const account = accountId ? accounts?.find((a) => a.id === accountId) : undefined
    // Never surface the underlying acquirer (Adyen/Stripe/…) to the merchant —
    // show the neutral, numbered Processing Network (contract identity) instead.
    const provider = processingNetworkLabel(account?.payfacProvider)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('tagadapayRouter.configurationTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('tagadapayRouter.accountLabel')}</p>
                        {accountId ? (
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span className="font-medium">{provider}</span>
                                <TpaLiveStatus
                                    tpaId={accountId}
                                    status={account?.status}
                                    mode={account?.mode}
                                />
                            </div>
                        ) : (
                            <p className="font-mono text-sm">{t('notConfigured')}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">3D Secure</p>
                        <p className="mt-1 font-medium">
                            {THREE_DS_MODE_LABELS[threeDSMode] ?? threeDSMode}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
